import { useEffect, useState } from "react";
import {
  AlertTriangle, FileText, Gift, Globe2, Headphones, Heart, MessageCircle, Package,
  PackageCheck, Plane, Search, Shirt, ShieldCheck, Truck, UtensilsCrossed,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { DestinationCard, FinalCta, QuoteTrackButtons, SectionHead, StepsLine, usePageMeta } from "../components/service/ServiceBits";
import api from "../lib/api";
import VideoHeroBg from "../components/service/VideoHeroBg";

const DESTINATIONS = [
  { name: "USA", img: "https://images.unsplash.com/photo-1496588152823-86ff7695e68f?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Canada", img: "https://images.unsplash.com/photo-1517935706615-2717063c2225?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "UK", img: "https://images.unsplash.com/photo-1486299267070-83823f5448dd?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Australia", img: "https://images.unsplash.com/photo-1549180030-48bf079fb38a?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "New Zealand", img: "https://images.unsplash.com/photo-1531804226530-70f8004aa44e?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Europe", img: "https://images.unsplash.com/photo-1570097703229-b195d6dd291f?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Middle East", img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
];

const SEND_ITEMS = [
  { icon: UtensilsCrossed, title: "Indian Food", desc: "Permitted food products, subject to destination regulations.",
    img: "https://images.unsplash.com/photo-1695568180783-003f8dcf4a55?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: Package, title: "Pickles & Snacks", desc: "Where permitted by destination-country rules.",
    img: "https://images.pexels.com/photos/34153206/pexels-photo-34153206.jpeg?auto=compress&cs=tinysrgb&w=800" },
  { icon: Shirt, title: "Clothes", desc: "Traditional and everyday clothing for your family.",
    img: "https://images.unsplash.com/photo-1735353783421-19a3b6f0bdce?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: Gift, title: "Gifts", desc: "Personal gifts for family and friends abroad.",
    img: "https://images.pexels.com/photos/8819064/pexels-photo-8819064.jpeg?auto=compress&cs=tinysrgb&w=800" },
  { icon: FileText, title: "Documents", desc: "Important personal documents, delivered securely.",
    img: "https://images.unsplash.com/photo-1449247666642-264389f5f5b1?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: Heart, title: "Personal Items", desc: "Permitted personal belongings packed with care.",
    img: "https://images.pexels.com/photos/27176165/pexels-photo-27176165.jpeg?auto=compress&cs=tinysrgb&w=800" },
];

const LAYERS = [
  { label: "Food / Product", color: "bg-saffron text-white", w: "w-2/5" },
  { label: "Protective Inner Packing", color: "bg-express text-white", w: "w-3/5" },
  { label: "Waterproof Protection", color: "bg-navy text-white", w: "w-4/5" },
  { label: "Strong Outer Packing", color: "bg-emerald-600 text-white", w: "w-full" },
];

const JOURNEY = [
  { n: "01", title: "Pack at Home", desc: "Gather the food, gifts and memories you want to send." },
  { n: "02", title: "IndiaFast Pickup", desc: "We collect the parcel from your doorstep." },
  { n: "03", title: "Professional Packing", desc: "Extra-care, multi-layer packing for international transit." },
  { n: "04", title: "International Transit", desc: "Your parcel flies out through our courier network." },
  { n: "05", title: "Customs", desc: "Documentation and clearance handled with guidance." },
  { n: "06", title: "Delivery to Your Loved One", desc: "A piece of home, delivered to their door." },
];

const FESTIVALS = ["Sankranti", "Ugadi", "Rakhi", "Diwali", "Christmas", "New Year"];

const WHY = [
  { icon: Truck, title: "Door-to-Door Delivery", desc: "From your home in India to their home abroad." },
  { icon: PackageCheck, title: "Professional Packing", desc: "Extra-care packing for food and fragile items." },
  { icon: Globe2, title: "International Courier Network", desc: "Trusted courier partners across 200+ countries." },
  { icon: Search, title: "Shipment Tracking", desc: "You and your family can follow every step." },
  { icon: Headphones, title: "Customer Support", desc: "Guidance on what can be sent, and how." },
  { icon: Plane, title: "Multiple Service Options", desc: "Express and economy choices for every budget." },
];

export default function NriCourier() {
  const [contact, setContact] = useState(null);
  usePageMeta(
    "NRI Courier Service — Send Food, Gifts & Parcels Abroad | IndiaFast",
    "Send Indian food, festive gifts, clothes, documents and personal items from India to your loved ones in the USA, Canada, UK, Australia, Europe and the Middle East.",
    "/nri-courier"
  );
  useEffect(() => { api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {}); }, []);
  const waNum = (contact?.phone || "").replace(/\D/g, "").slice(-10);
  const waLink = waNum ? `https://wa.me/91${waNum}?text=${encodeURIComponent("Hi IndiaFast, I want to send an NRI parcel abroad. Please help.")}` : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="nri-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-24 pt-32 sm:pt-36" data-testid="nri-hero">
        <VideoHeroBg mp4="/videos/bike.mp4" webm="/videos/bike.webm"
                     poster="https://static.prod-images.emergentagent.com/jobs/6e217b82-4821-4792-98ef-42642f657e6a/images/dd6e5bf5b39e54d613fb3dbc63322f0228457d2732b654eec49c1dccac8793af.jpeg"
                     posterAlt="IndiaFast pickup rider with branded delivery box" badge="Doorstep Pickup" chip="AWB IFAST1000003 · Picked up" testid="nri-hero-video" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="animate-fade-up text-xs font-bold uppercase tracking-[0.25em] text-saffron">NRI Courier</p>
          <h1 className="animate-fade-up stagger-1 mt-3 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Send a Piece of <span className="text-saffron">Home</span> to Your Loved Ones Abroad
          </h1>
          <p className="animate-fade-up stagger-2 mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            From Indian food and festive gifts to personal belongings and important documents — send from India to family and friends around the world.
          </p>
          <div className="animate-fade-up stagger-3 mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
            <Link to="/register" data-testid="nri-send-cta"
                  className="flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">
              Send an NRI Parcel
            </Link>
            <Link to="/rate-calculator" data-testid="nri-quote-cta"
                  className="flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/10 px-8 py-3.5 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/20">
              Get a Quote
            </Link>
          </div>
        </div>
      </section>

      {/* NRI DESTINATIONS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="nri-destinations">
        <SectionHead kicker="NRI Destinations" title="Wherever Your Family Is, We Reach Them" />
        <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          {DESTINATIONS.map((d) => (
            <DestinationCard key={d.name} name={d.name} img={d.img} testid={`nri-dest-${d.name.toLowerCase().replace(/\s+/g, "-")}`} />
          ))}
        </div>
      </section>

      {/* WHAT CAN YOU SEND */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="nri-send-items">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="What Can You Send?" title="Everything That Says 'Home'" />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SEND_ITEMS.map((s, i) => (
              <div key={i} data-testid={`nri-item-${i}`}
                   className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl">
                <div className="relative h-40 overflow-hidden">
                  <img src={s.img} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute left-4 top-4 grid h-10 w-10 place-items-center rounded-xl bg-navy/90 text-saffron backdrop-blur"><s.icon className="h-5 w-5" /></span>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-bold text-navy">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3-LAYER PACKING */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="nri-packing">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-3xl">
            <img src="https://images.pexels.com/photos/11781094/pexels-photo-11781094.jpeg?auto=compress&cs=tinysrgb&w=1200"
                 alt="Professional packing with bubble wrap and fragile protection" loading="lazy" className="h-72 w-full object-cover sm:h-[420px]" />
            <span className="tricolor-beam absolute inset-x-0 bottom-0 h-1.5" />
          </div>
          <div>
            <SectionHead kicker="Special NRI Packing" title="Packed With Extra Care"
                         sub="Where applicable, NRI parcels get 3-layer packing so food and fragile items travel safely across the world." />
            <div className="mt-8 space-y-2.5" data-testid="nri-packing-layers">
              {LAYERS.map((l, i) => (
                <div key={i} className={`mx-auto rounded-xl px-4 py-3 text-center text-xs font-bold shadow-sm ${l.color} ${l.w}`}
                     data-testid={`nri-layer-${i}`}>
                  {l.label}
                </div>
              ))}
              <p className="pt-1 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400">3-Layer NRI Packing · Where applicable</p>
            </div>
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="bg-white py-16 sm:py-20" data-testid="nri-journey">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="NRI Shipping Journey" title="From Your Kitchen to Their Doorstep" center />
          <StepsLine steps={JOURNEY} testid="nri-steps" cols="lg:grid-cols-6" />
        </div>
      </section>

      {/* FESTIVALS */}
      <section className="relative overflow-hidden bg-navy py-16 sm:py-20 lg:py-24" data-testid="nri-festivals">
        <img src="https://images.unsplash.com/photo-1730130596425-197566414dc4?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
             alt="Indian family celebrating a festival at home" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/85 to-navy" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Festivals</p>
          <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Make Every Festival Feel Closer
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Homemade sweets for Diwali, a rakhi for your brother, festive clothes for the kids — some things shouldn't have to wait for a visit home.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {FESTIVALS.map((f, i) => (
              <span key={f} data-testid={`nri-festival-${i}`}
                    className="rounded-full border border-saffron/30 bg-saffron/10 px-5 py-2.5 text-sm font-bold text-saffron backdrop-blur">
                {f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FOOD DISCLAIMER */}
      <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6" data-testid="nri-food-info">
        <div className="flex gap-4 rounded-2xl border border-amber-300 bg-amber-50 p-6">
          <AlertTriangle className="h-6 w-6 shrink-0 text-amber-600" />
          <div>
            <p className="font-heading text-sm font-bold text-amber-800">Important: Food Shipping Regulations</p>
            <p className="mt-1.5 text-sm leading-relaxed text-amber-700">
              Food shipments are subject to destination-country customs, quarantine, food-import regulations and courier restrictions.
              Acceptance depends on the product, destination and applicable regulations — our team will confirm what can be sent before you book.
            </p>
          </div>
        </div>
      </section>

      {/* WHY INDIAFAST NRI */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="nri-why">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="Why IndiaFast for NRI Shipping" title="Handled Like It's Going to Our Own Family" />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((w, i) => (
              <div key={i} data-testid={`nri-why-${i}`}
                   className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all hover:-translate-y-1 hover:border-saffron/40 hover:bg-white hover:shadow-xl">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><w.icon className="h-6 w-6" /></span>
                <h3 className="mt-4 font-heading text-base font-bold text-navy">{w.title}</h3>
                <p className="mt-1.5 text-sm text-slate-500">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCta testid="nri-final-cta"
                img="https://images.pexels.com/photos/36813401/pexels-photo-36813401.jpeg?auto=compress&cs=tinysrgb&w=1920"
                title="Because Distance Shouldn't Stop You From Sending Love From Home."
                sub="Send your next parcel with IndiaFast Global Couriers."
                primaryLabel="Get NRI Quote" primaryTo="/rate-calculator" primaryTestid="nri-final-quote"
                secondary={waLink && (
                  <a href={waLink} target="_blank" rel="noreferrer" data-testid="nri-final-whatsapp"
                     className="flex items-center justify-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-600 px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-500">
                    <MessageCircle className="h-4 w-4" /> Talk on WhatsApp
                  </a>
                )} />
      <Footer contact={contact} />
    </div>
  );
}
